import React, { useEffect, useRef } from 'react';
import { useSettings } from './SettingsContext';
import { FontFamily } from './SettingsContext';

/**
 * FontProvider - Applies font settings globally to the app
 *
 * On web: Injects CSS to override font styles
 */
export const FontProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <FontApplier>{children}</FontApplier>;
};

let styleElement: HTMLStyleElement | null = null;

const FontApplier: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { fontSettings } = useSettings();
  const appliedSettingsRef = useRef(`${fontSettings.fontSize}-${fontSettings.fontFamily}`);

  const getFontFamilyValue = (family: FontFamily): string => {
    switch (family) {
      case 'system':
        return 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif';
      case 'system-ui':
        return 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif';
      case 'sans-serif':
        return 'sans-serif';
      case 'serif':
        return 'serif';
      case 'monospace':
        return 'ui-monospace, Monaco, "Cascadia Code", "Roboto Mono", Courier, monospace';
      default:
        // Custom fonts - use the font name directly
        return `"${family}", system-ui, -apple-system, sans-serif`;
    }
  };

  useEffect(() => {
    const currentSettingsKey = `${fontSettings.fontSize}-${fontSettings.fontFamily}`;
    // Skip if settings haven't changed
    if (appliedSettingsRef.current === currentSettingsKey) {
      return;
    }
    appliedSettingsRef.current = currentSettingsKey;

    const fontSize = fontSettings.fontSize;
    const fontFamily = getFontFamilyValue(fontSettings.fontFamily);

    if (typeof document !== 'undefined') {
      // Create style element if it doesn't exist
      if (!styleElement) {
        styleElement = document.createElement('style');
        styleElement.id = 'dream-catcher-font-styles';
        document.head.appendChild(styleElement);
      }

      // CSS custom properties for components to use
      const css = `
        /* Font settings as CSS custom properties for components to use */
        :root {
          --app-font-size: ${fontSize}px;
          --app-font-family: ${fontFamily};
        }

        /* Eagle Lake font for titles - NEVER changes, independent of user font setting */
        .fantasy-font,
        .dream-catcher-title,
        [class*="dream-catcher-title"] {
          font-family: 'Eagle Lake', 'EagleLake', serif !important;
        }

        /* Input placeholders use user's font - GLOBAL + specific class approach */
        ::placeholder,
        ::-webkit-input-placeholder,
        ::-moz-placeholder,
        :-ms-input-placeholder {
          font-family: var(--app-font-family) !important;
          font-size: var(--app-font-size) !important;
        }

        /* App text class - can be applied to any text element */
        .app-text {
          font-family: var(--app-font-family) !important;
          font-size: var(--app-font-size) !important;
        }

        /* App placeholder class - HIGHEST SPECIFICITY */
        input.app-input::placeholder,
        input.app-input::-webkit-input-placeholder,
        input.app-input::-moz-placeholder,
        input.app-input::-ms-input-placeholder,
        textarea.app-input::placeholder,
        textarea.app-input::-webkit-input-placeholder,
        textarea.app-input::-moz-placeholder,
        textarea.app-input::-ms-input-placeholder {
          font-family: var(--app-font-family) !important;
          font-size: var(--app-font-size) !important;
        }

        /* Also target all inputs with app-input class universally */
        .app-input {
          font-family: var(--app-font-family) !important;
        }
        .app-input::placeholder {
          font-family: var(--app-font-family) !important;
          font-size: var(--app-font-size) !important;
        }
      `;
      styleElement.textContent = css;
    }
  }, [fontSettings.fontSize, fontSettings.fontFamily]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (styleElement && typeof document !== 'undefined') {
        document.head.removeChild(styleElement);
        styleElement = null;
      }
    };
  }, []);

  return <>{children}</>;
};
